import { differenceInCalendarDays, getYear, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import type { Collaborator, PaidRestPeriod } from '@/features/Collaborators/Models/collaborator';
import { createDateSafe } from '@/utils/dateUtils';
import type { PaidRestRow, PaidRestCounts } from '../models/vacationPaidRest.types';

export function isOnRestNow(periods: PaidRestPeriod[]): boolean {
    const today = new Date();
    return periods.some((p) => {
        const start = createDateSafe(p.startDate);
        const end = createDateSafe(p.endDate);
        if (!start || !end) return false;
        return isWithinInterval(today, { start: startOfDay(start), end: endOfDay(end) });
    });
}

export function buildPaidRestRows(collaborators: Collaborator[] | undefined): PaidRestRow[] {
    if (!collaborators || collaborators.length === 0) return [];

    return collaborators
        .filter((c) => c.collaboratorType === 'Fornecedor')
        .map((c) => {
            const periods = c.paidRestPeriods ?? [];
            const currentYear = new Date().getFullYear();

            // Ordenar por data decrescente para pegar o mais recente
            const sortedPeriods = [...periods].sort((a, b) => {
                const dateA = createDateSafe(a.startDate);
                const dateB = createDateSafe(b.startDate);
                if (!dateA || !dateB) return 0;
                return dateB.getTime() - dateA.getTime();
            });

            const lastPeriod = sortedPeriods.length > 0 ? sortedPeriods[0] : null;

            // Calcular total de dias no ano (apenas os dias que caem dentro do ano)
            const totalDaysYear = periods.reduce((sum, p) => {
                const start = createDateSafe(p.startDate);
                const end = createDateSafe(p.endDate);
                if (!start || !end) return sum;

                const startYear = getYear(start);
                const endYear = getYear(end);

                // Se o período fica completamente dentro do ano
                if (startYear === currentYear && endYear === currentYear) {
                    return sum + (differenceInCalendarDays(end, start) + 1);
                }

                // Se o período cruza o ano, contar apenas os dias dentro do ano atual
                if (startYear < currentYear && endYear >= currentYear) {
                    // Contar do primeiro dia do ano até o fim do período
                    const yearStart = new Date(currentYear, 0, 1);
                    const periodEnd = differenceInCalendarDays(end, yearStart) + 1;
                    return sum + Math.max(0, Math.min(periodEnd, 365)); // No máximo 365 dias
                }

                if (startYear <= currentYear && endYear > currentYear) {
                    // Contar do início do período até o último dia do ano
                    const yearEnd = new Date(currentYear, 11, 31);
                    const daysUntilYearEnd = differenceInCalendarDays(yearEnd, start) + 1;
                    return sum + Math.max(0, daysUntilYearEnd);
                }

                return sum;
            }, 0);

            return {
                collaboratorId: c.id ?? '',
                collaboratorName: c.name ?? '',
                cnpj: c.cnpj,
                companyName: c.companyName,
                hasPaidRest: c.hasPaidRest ?? false,
                paidRestObservations: c.paidRestObservations,
                paidRestPeriods: periods,
                lastPeriod: lastPeriod ?? null,
                totalDaysYear,
            };
        });
}

export function formatPaidRestPeriod(startDate: string, endDate: string): string {
    const start = createDateSafe(startDate);
    const end = createDateSafe(endDate);

    if (!start || !end) return '—';

    const days = differenceInCalendarDays(end, start) + 1;
    return `${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')} (${days}d)`;
}

export function formatPaidRestLastPeriod(period: PaidRestPeriod | null): string {
    if (!period) return 'Nunca lançado';
    return formatPaidRestPeriod(period.startDate, period.endDate);
}

export function countPaidRestBuckets(rows: PaidRestRow[]): PaidRestCounts {
    const counts: PaidRestCounts = {
        inRest: 0,
        offRest: 0,
        withRight: 0,
        withoutRight: 0,
    };

    rows.forEach((row) => {
        if (isOnRestNow(row.paidRestPeriods ?? [])) {
            counts.inRest += 1;
        } else {
            counts.offRest += 1;
        }

        if (row.hasPaidRest) {
            counts.withRight += 1;
        } else {
            counts.withoutRight += 1;
        }
    });

    return counts;
}
